import React, { RefObject, useCallback, useMemo, useRef } from 'react';

import { LayoutChangeEvent, View } from 'react-native';

import { useSelector } from 'react-redux';

import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';
import { useFocusEffect } from '@react-navigation/core';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';

import { EpgLayout } from 'store/epg/reducer.types';
import { selectPlayerConfig } from 'store/player/selectors';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import useHardwareBackPressHandler from 'utils/hooks/useHardwareBackPressHandler';
import useHeaderHeight from 'utils/hooks/useHeaderHeight';
import { isTablet, isWeb, widthPercentageToDP } from 'utils/responsiveness';

import useDatePicker, { Position } from 'screens/Epg/hooks/useDatePicker';
import { EpgFilterCategory } from 'screens/Epg/types';

import EpgFilter from '../EpgFilter';
import EpgGridDatePicker from '../EpgGridDatePicker';
import EpgLayoutButton from '../EpgLayoutButton';
import styles from './styles';

type Props = {
    screenReaderEnabled: boolean;
    epgSelectedLayout?: EpgLayout;
    onChangeEpgLayout: (newLayout: EpgLayout) => void;
    selectedFilterCategory?: EpgFilterCategory;
    filterBottomSheetRef: RefObject<BottomSheetModalMethods | undefined>;
    filterCategories: EpgFilterCategory[];
    isFilterListVisible: boolean;
    filterCategorySelected: boolean;
    onSelectFilterCategory: (filterCategory: EpgFilterCategory) => void;
    onShowFilterList: () => void;
    onCloseFilterList: () => void;
};

const PICKER_MARGIN = isWeb
    ? widthPercentageToDP(72)
    : widthPercentageToDP(16, 44);

const {
    EPG: {
        ACCESSIBILITY_EPG_SWITCHER_OLD_LAYOUT,
        ACCESSIBILITY_EPG_SWITCHER_NEW_LAYOUT,
    },
} = LANGUAGE_KEYS;

function EpgHeader({
    screenReaderEnabled,
    epgSelectedLayout,
    onChangeEpgLayout,
    selectedFilterCategory,
    filterBottomSheetRef,
    filterCategories,
    isFilterListVisible,
    filterCategorySelected,
    onSelectFilterCategory,
    onShowFilterList,
    onCloseFilterList,
}: Props) {
    const datePickerRef = useRef<View>(null);
    const { visible: playerVisible } = useSelector(selectPlayerConfig);

    const {
        selectedDateLabel,
        bottomSheetModalRef,
        handleOpen: handleOpenDatePicker,
        dateData,
        isModalVisible: isDatePickerListVisible,
        handleClose: handleCloseDatePicker,
        onPressDatePickerItem,
    } = useDatePicker({ current: null }, 250, screenReaderEnabled);

    const handleHardwareBackPress = useCallback(() => {
        if (isDatePickerListVisible) {
            handleCloseDatePicker();
        }

        if (isFilterListVisible) {
            onCloseFilterList();
        }
    }, [
        handleCloseDatePicker,
        isDatePickerListVisible,
        isFilterListVisible,
        onCloseFilterList,
    ]);

    useHardwareBackPressHandler(handleHardwareBackPress);

    const headerHeight = useHeaderHeight();

    const [datePickerWidth, setDatePickerWidth] = React.useState(0);

    const datePickerStartingPosition = useMemo<Position | undefined>(
        () => ({
            left: PICKER_MARGIN,
            top: headerHeight + styles.root.height,
        }),
        [headerHeight],
    );

    const filterStartingPosition = useMemo<Position | undefined>(
        () => ({
            // @ts-ignore
            left: isTablet ? 'auto' : PICKER_MARGIN + datePickerWidth,
            right: isTablet ? PICKER_MARGIN : 'auto',
            top: headerHeight + styles.root.height,
        }),
        [headerHeight, datePickerWidth],
    );

    const handlePickerDismiss = useCallback(() => null, []);

    const handleChangeEpgLayout = useCallback(
        (newLayout: EpgLayout) => {
            onChangeEpgLayout(newLayout);
        },
        [onChangeEpgLayout],
    );

    const handleDatePickerLayoutChange = useCallback((e: LayoutChangeEvent) => {
        setDatePickerWidth(e.nativeEvent.layout.width);
    }, []);

    useFocusEffect(
        useCallback(() => {
            if (epgSelectedLayout === EpgLayout.LIST) {
                if (isWeb) {
                    setAccessibilityFocusTo(datePickerRef);
                } else if (screenReaderEnabled) {
                    setTimeout(() => {
                        setAccessibilityFocusTo(datePickerRef);
                    }, 200);
                }
            }
        }, [epgSelectedLayout, screenReaderEnabled]),
    );

    return (
        <View style={[gStyles.flexRow, styles.root]}>
            <View style={[gStyles.flexRow, styles.pickerContainer]}>
                {epgSelectedLayout === EpgLayout.LIST && (
                    <>
                        <View
                            style={styles.datePickerContainer}
                            onLayout={handleDatePickerLayoutChange}
                            importantForAccessibility={
                                playerVisible ? 'no-hide-descendants' : 'auto'
                            }
                        >
                            <EpgGridDatePicker
                                ref={datePickerRef}
                                handleOpen={handleOpenDatePicker}
                                selectedDateLabel={selectedDateLabel}
                                data={dateData}
                                onClose={handleCloseDatePicker}
                                isModalVisible={isDatePickerListVisible}
                                modalStartingPosition={
                                    datePickerStartingPosition
                                }
                                bottomSheetModalRef={bottomSheetModalRef}
                                screenReaderEnabled={screenReaderEnabled}
                                onPressDatePickerItem={onPressDatePickerItem}
                                onDismiss={handlePickerDismiss}
                                isListLayout
                            />
                        </View>
                        <View
                            style={styles.filterPickerContainer}
                            importantForAccessibility={
                                playerVisible ? 'no-hide-descendants' : 'auto'
                            }
                        >
                            <EpgFilter
                                bottomSheetRef={filterBottomSheetRef}
                                filterCategories={filterCategories}
                                filterCategorySelected={filterCategorySelected}
                                isFilterListVisible={isFilterListVisible}
                                screenReaderEnabled={screenReaderEnabled}
                                onPressFilter={onShowFilterList}
                                onCloseFilter={onCloseFilterList}
                                onPressFilterCategoryItem={
                                    onSelectFilterCategory
                                }
                                onDismiss={handlePickerDismiss}
                                selectedFilterCategory={
                                    selectedFilterCategory?.label || ''
                                }
                                modalStartingPosition={filterStartingPosition}
                                isListLayout
                            />
                        </View>
                    </>
                )}
            </View>
            {isWeb && (
                <View style={[gStyles.flexRow, styles.layoutButtonsContainer]}>
                    <EpgLayoutButton
                        type={EpgLayout.GRID}
                        selected={epgSelectedLayout === EpgLayout.GRID}
                        onButtonPress={handleChangeEpgLayout}
                        style={styles.layoutButton}
                        accessibilityLabel={
                            ACCESSIBILITY_EPG_SWITCHER_OLD_LAYOUT
                        }
                    />
                    <EpgLayoutButton
                        type={EpgLayout.LIST}
                        selected={epgSelectedLayout === EpgLayout.LIST}
                        onButtonPress={handleChangeEpgLayout}
                        accessibilityLabel={
                            ACCESSIBILITY_EPG_SWITCHER_NEW_LAYOUT
                        }
                    />
                </View>
            )}
        </View>
    );
}

export default EpgHeader;
